import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { siteConfig } from '../../data/config';
import { Phone, Clock, CreditCard, Car, Apple, Play } from 'lucide-react';

const Footer = () => {
  const { t, getRoute } = useLanguage();

  return (
    <footer className="bg-foreground text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img 
                src="/images/mtl-taxi-logo-electric-suv-montreal.png"
                alt="MTL.TAXI"
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-6">{t('footer.tagline')}</p>
            
            <div className="space-y-3">
              <a 
                href={siteConfig.phoneTel} 
                className="flex items-center gap-3 text-accent hover:text-accent/80 transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
                <span className="font-semibold">{siteConfig.phone}</span>
              </a>
              <div className="flex items-center gap-3 text-gray-400">
                <Clock className="w-5 h-5" />
                <span>{siteConfig.hours}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <CreditCard className="w-5 h-5" />
                <span>{siteConfig.paymentMethods.join(', ')}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to={getRoute('/')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/airport')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.airport')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/services')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/fares')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.fares')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/areas')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.areas')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/faq')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.faq')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/contact')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to={getRoute('/privacy')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/terms')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to={getRoute('/disclaimer')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.disclaimer')}
                </Link>
              </li>
            </ul>
          </div>

          {/* App Download */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('contact.appTitle')}</h3>
            <p className="text-gray-400 mb-4 text-sm">{t('contact.appDesc')}</p>
            <div className="flex flex-col gap-3">
              <a
                href={siteConfig.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-lg"
                data-testid="footer-appstore"
              >
                <Apple className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-xs text-gray-400">Download on the</div>
                  <div className="font-semibold text-sm">App Store</div>
                </div>
              </a>
              <a
                href={siteConfig.playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-lg"
                data-testid="footer-playstore"
              >
                <Play className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-xs text-gray-400">Get it on</div>
                  <div className="font-semibold text-sm">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} {siteConfig.businessName}. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
